import PropTypes from 'prop-types';

import { IconCheckmark, Spacer } from '@zillow/constellation';
import { Paragraph, Table, Text, Tooltip } from '@zillow/constellation-10';

const propTypes = {
  onOpenLostRentTooltip: PropTypes.func.isRequired,
  onOpenUnitDamageTooltip: PropTypes.func.isRequired,
  marginBottom: PropTypes.number,
  title: PropTypes.element,
};

const defaultProps = {
  marginBottom: 9,
};

const RentalProtectionCoverageTableSection = ({
  onOpenLostRentTooltip,
  onOpenUnitDamageTooltip,
  title,
  marginBottom,
}) => {
  return (
    <div data-testid="RentalProtectionCoverageTableSection">
      {title ? (
        title
      ) : (
        <Paragraph
          css={{
            textStyle: 'heading-sm',
            marginBottom: '200',
            fontFamily: 'heading-alt',
            color: 'text.express.insight.strong',
          }}
        >
          Complete your rental coverage
        </Paragraph>
      )}
      <Paragraph css={{ marginBottom: '400' }}>
        Rental Protection picks up where your landlord property insurance leaves off.
      </Paragraph>
      <Paragraph>Here&apos;s how your policies could work together:</Paragraph>
      <Spacer marginTop="md" marginBottom={marginBottom}>
        <Table.Root
          appearance="horizontal"
          aria-label="Medium-sized horizontal table"
          size="md"
          alignments={['left', 'center', 'center']}
          css={{
            marginTop: '600',
            marginBottom: '1200',
            lgDown: {
              '& td': {
                padding: '12px 0px',
              },
            },
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell></Table.HeaderCell>
              <Table.HeaderCell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>Rental Protection</Text>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>Landlord insurance*</Text>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Lost rent if a renter can&apos;t make a payment
                </Text>
              </Table.HeaderCell>
              <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                <IconCheckmark size="sm" fontColor="brand" />
              </Table.Cell>
              <Table.Cell css={{ verticalAlign: 'middle' }}></Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>Pet damage to unit</Text>
              </Table.HeaderCell>
              <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                <IconCheckmark size="sm" fontColor="brand" />
              </Table.Cell>
              <Table.Cell css={{ verticalAlign: 'middle' }}></Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>Renter damage to unit</Text>
              </Table.HeaderCell>
              <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                <IconCheckmark size="sm" fontColor="brand" />
              </Table.Cell>
              <Table.Cell css={{ verticalAlign: 'middle' }}></Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Costs and legal fees associated with eviction
                </Text>
              </Table.HeaderCell>
              <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                <IconCheckmark size="sm" fontColor="brand" />
              </Table.Cell>
              <Table.Cell css={{ verticalAlign: 'middle' }}></Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Lost rent due to other causes{' '}
                  <Tooltip
                    content="Dwelling insurance typically covers lost rent if your property becomes uninhabitable due to lightning, wind, fire, smoke, or other covered perils."
                    onOpenChange={(open) => open && onOpenLostRentTooltip()}
                  />
                </Text>
              </Table.HeaderCell>
              <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}></Table.Cell>
              <Table.Cell css={{ verticalAlign: 'middle' }}>
                <IconCheckmark size="sm" />
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Damage to unit by other causes{' '}
                  <Tooltip
                    content="Dwelling insurance typically covers repairs for sudden, accidental damage to your property due to lightning, wind, fire, smoke, or other covered perils."
                    onOpenChange={(open) => open && onOpenUnitDamageTooltip()}
                  />
                </Text>
              </Table.HeaderCell>
              <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}></Table.Cell>
              <Table.Cell css={{ verticalAlign: 'middle' }}>
                <IconCheckmark size="sm" />
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Personal liability coverage for landlord
                </Text>
              </Table.HeaderCell>
              <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}></Table.Cell>
              <Table.Cell css={{ verticalAlign: 'middle' }}>
                <IconCheckmark size="sm" />
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Damage to landlord personal property
                </Text>
              </Table.HeaderCell>
              <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}></Table.Cell>
              <Table.Cell css={{ verticalAlign: 'middle' }}>
                <IconCheckmark size="sm" />
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      </Spacer>
    </div>
  );
};

RentalProtectionCoverageTableSection.propTypes = propTypes;
RentalProtectionCoverageTableSection.defaultProps = defaultProps;

export default RentalProtectionCoverageTableSection;
